import React from "react";
import GitRefDropDown from "~/areas/projects/components/GitRefDropDown/GitRefDropDown";
import type { GitRefOption } from "~/areas/projects/components/GitRefDropDown/GitRefOption";
import type { WithProjectContextInjectedProps } from "~/areas/projects/context";
import { useProjectContext } from "~/areas/projects/context";
import type { ProjectResource, VcsBranchResource } from "~/client/resources";
import { HasVersionControlledPersistenceSettings } from "~/client/resources";
import type { GitReference } from "~/client/resources/versionControlledResource";
import { repository } from "~/clientInstance";
import { buildBranchNamesList, getTextValuePairForBranch } from "~/utils/BranchHelpers/BranchHelpers";

interface GitRefSelectorProps extends WithProjectContextInjectedProps {
    vcsRef?: GitReference;
    onChange: (ref: GitReference) => void;
    disabled?: boolean;
}

const ensureSelectedAndDefaultItemExists = async (selectedGitRef: string, collection: GitRefOption[], project: Readonly<ProjectResource>) => {
    if (!HasVersionControlledPersistenceSettings(project.PersistenceSettings)) throw new Error("Config as Code: Trying to access a VCS Property on a non-VCS Project.");
    const vcsPersistenceSettings = project.PersistenceSettings;

    let items: GitRefOption[] = collection;
    if (selectedGitRef) {
        if (!items.find((item) => item.value === selectedGitRef)) {
            const branchResource = await repository.Projects.getBranch(project, selectedGitRef);
            items = [getTextValuePairForBranch(branchResource, vcsPersistenceSettings), ...items];
        }
    }

    if (!items.find((item) => item.value === vcsPersistenceSettings.DefaultBranch)) {
        const branchResource = await repository.Projects.getBranch(project, vcsPersistenceSettings.DefaultBranch);
        items = [getTextValuePairForBranch(branchResource, vcsPersistenceSettings), ...items];
    }

    return items;
};

const GitRefSelector: React.FC<GitRefSelectorProps> = (props) => {
    const project = useProjectContext().state.model;
    const [vcsRef, setVcsRef] = React.useState(props.vcsRef);
    const [items, setItems] = React.useState<GitRefOption[]>([]);
    const [totalBranches, setTotalBranches] = React.useState<number>(0);

    const onCreateBranch = async (branchName: string) => {
        await props.projectContext.state.projectContextRepository.Branches.createBranch(project, branchName, props.projectContext.state.branch?.Name ?? "");
        branchSelected({ value: branchName, text: "" });
    };

    const branchSelected = (gitRef: GitRefOption) => {
        const vcsRef = {
            GitRef: gitRef.value,
        };
        setVcsRef(vcsRef);
        props.onChange(vcsRef);
    };

    const refresh = async () => {
        const selectedBranch = props.projectContext.state.branch;
        if (!selectedBranch) return;

        await repository.Projects.markAsStale(project);
        const branchResources = await repository.Projects.getBranches(project);
        const items = await ensureSelectedAndDefaultItemExists(selectedBranch.Name, buildBranchNamesList(branchResources.Items, project), project);
        setItems(items);
        setTotalBranches(branchResources.TotalResults);
        props.projectContext.actions.onBranchSelected(project, selectedBranch.Name);
    };

    const search = async (value: string) => {
        const branchResources = await repository.Projects.searchBranches(project, value);
        return buildBranchNamesList(branchResources.Items, project);
    };

    React.useEffect(() => {
        async function fetchGitRefs(branch: VcsBranchResource) {
            const branchResources = await repository.Projects.getBranches(project);

            // Note: there is a chance that the resource passed to us isn't in the collection returned by getBranches, as it only returns the latest `n` active branches
            // Note: if it isn't in the collection retrieved, we will insert it here
            setItems(await ensureSelectedAndDefaultItemExists(branch.Name, buildBranchNamesList(branchResources.Items, project), project));
            setTotalBranches(branchResources.TotalResults);
        }

        if (project && project.IsVersionControlled && props.projectContext.state.branch) {
            fetchGitRefs(props.projectContext.state.branch);
        }
    }, [project, props.projectContext.state.branch]);

    return (
        <>
            {/* This will eventually be tabs, allowing selecting a branch or tag */}
            <GitRefDropDown
                style="white"
                value={vcsRef?.GitRef}
                items={items}
                totalItems={totalBranches}
                empty="No branches found"
                onChange={branchSelected}
                onRequestRefresh={refresh}
                onFilterChanged={search}
                disabled={props.disabled}
                onCreateBranch={onCreateBranch}
                projectId={project.Id}
            />
        </>
    );
};

export default GitRefSelector;
